import { useState, useEffect, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../components/Modal';
import {
  DAMAGE_TYPES, DAMAGE_LOCATIONS, DAMAGE_SEVERITIES, DAMAGE_STATUSES, DAMAGE_PAID_BY,
} from '../data/damages';

const MAX_PHOTOS = 6;

const BLANK_FORM = {
  vid: '',
  rentalId: '',
  customerId: '',
  customerName: '',
  type: 'scratch',
  location: '',
  severity: 'minor',
  description: '',
  reportedDate: '',
  photos: [],
  estimatedCost: '',
  finalCost: '',
  status: 'reported',
  paidBy: 'unpaid',
  invoiceId: '',
  insuranceClaim: '',
  resolvedDate: '',
  notes: '',
  repaired: false,       // physical state — independent from payment status
  repairedDate: '',
};

export default function DamageModal() {
  const {
    modals, closeModal, openModal,
    vehicles, customers, rentals, damages, invoices,
    addDamage, updateDamage, deleteDamage,
    nextInvoiceNum, setInvoices,
    showToast, currentUserId, companySettings,
  } = useApp();

  const open  = modals['damage'];
  const dmgId = modals['damage-id'];
  const isEdit = !!(dmgId && typeof dmgId === 'string');
  const existing = isEdit ? damages.find(d => d.id === dmgId) : null;

  const [form, setForm] = useState(BLANK_FORM);
  const photoInputRef = useRef(null);
  const [photoSlotIdx, setPhotoSlotIdx] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setForm({ ...BLANK_FORM, ...existing });
    } else {
      // Allow pre-fill via openModal('damage', { vid, rentalId, customerId, customerName })
      const preset = typeof dmgId === 'object' && dmgId !== null ? dmgId : {};
      setForm({
        ...BLANK_FORM,
        reportedDate: new Date().toISOString().slice(0, 10),
        ...preset,
      });
    }
  }, [open, dmgId]);

  if (!open) return null;

  function F(k) { return { value: form[k] ?? '', onChange: e => setForm(p => ({ ...p, [k]: e.target.value })) }; }

  function pickRental(rentalId) {
    setForm(p => {
      if (!rentalId) return { ...p, rentalId: '', customerId: '', customerName: '' };
      const r = rentals.find(rr => rr.id === rentalId);
      if (!r) return { ...p, rentalId };
      const cust = customers.find(c => `${c.fname} ${c.lname}`.trim() === (r.customer || '').trim());
      return {
        ...p,
        rentalId,
        vid: p.vid || r.vid,
        customerId: cust?.id || p.customerId,
        customerName: r.customer || p.customerName,
      };
    });
  }

  function onPhotoFile(e) {
    const file = e.target.files?.[0];
    if (!file || photoSlotIdx === null) return;
    const reader = new FileReader();
    reader.onload = ev => setForm(p => {
      const photos = [...(p.photos || [])];
      photos[photoSlotIdx] = ev.target.result;
      return { ...p, photos };
    });
    reader.readAsDataURL(file);
    e.target.value = '';
  }
  function openPhotoSlot(idx) { setPhotoSlotIdx(idx); photoInputRef.current?.click(); }
  function removePhoto(idx) {
    setForm(p => {
      const photos = [...(p.photos || [])];
      photos.splice(idx, 1);
      return { ...p, photos };
    });
  }

  function submit() {
    if (!form.vid)         { showToast('Please select a vehicle'); return; }
    if (!form.type)        { showToast('Please pick a damage type'); return; }
    if (!form.description) { showToast('Please describe the damage'); return; }

    const parsed = {
      ...form,
      estimatedCost: parseFloat(form.estimatedCost) || 0,
      finalCost:     form.finalCost === '' ? null : (parseFloat(form.finalCost) || 0),
      rentalId:      form.rentalId || null,
      customerId:    form.customerId || null,
      reportedBy:    currentUserId,
    };
    if (isEdit) {
      updateDamage(dmgId, parsed);
      showToast('Damage record updated');
    } else {
      addDamage(parsed);
      showToast('Damage reported');
    }
    closeModal('damage');
  }

  function handleDelete() {
    if (!isEdit) return;
    if (!window.confirm('Delete this damage record? This cannot be undone.')) return;
    deleteDamage(dmgId);
    showToast('Damage deleted');
    closeModal('damage');
  }

  function handleIssueInvoice() {
    if (!form.customerName || !form.vid) {
      showToast('Vehicle and customer required to issue invoice');
      return;
    }
    const cost = parseFloat(form.finalCost) || parseFloat(form.estimatedCost) || 0;
    if (cost <= 0) {
      showToast('Set a cost for this damage first');
      return;
    }
    const v = vehicles.find(vv => vv.id === form.vid);
    const num = nextInvoiceNum();
    const typeLabel = DAMAGE_TYPES.find(t => t.id === form.type)?.label || 'Damage';
    const vatRate = parseFloat(companySettings?.vatRate || 19);
    const vatMode = companySettings?.vatMode || 'inclusive';
    const subtotal = vatMode === 'inclusive' ? cost / (1 + vatRate / 100) : cost;
    const vatAmt   = vatMode === 'inclusive' ? cost - subtotal : cost * vatRate / 100;
    const total    = vatMode === 'inclusive' ? cost : cost + vatAmt;
    const today    = new Date().toISOString().slice(0, 10);
    const fmtDate  = (iso) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

    const inv = {
      id: 'inv-' + Date.now().toString(36),
      num, status: 'issued',
      customer: form.customerName,
      vehicle: v?.name || '',
      vid: form.vid,
      contract: form.rentalId || '',
      method: 'bank', methodLabel: 'Bank Transfer',
      issueDate: today, dueDate: today,
      issue: fmtDate(today), due: fmtDate(today),
      lines: [{
        desc: `Damage charge — ${typeLabel}${form.location ? ' (' + form.location + ')' : ''}`,
        qty: 1, unit: subtotal, total: subtotal,
      }],
      subtotal, vatRate, vatAmt, vatMode,
      discount: 0, discType: 'eur', total,
      notes: `Damage record: ${form.id || 'new'}`,
      issuedAt: new Date().toISOString(),
    };
    setInvoices(prev => [inv, ...prev]);
    // Link invoice back to the damage
    const updates = { invoiceId: num, status: 'charging', paidBy: 'customer' };
    if (isEdit) updateDamage(dmgId, updates);
    else setForm(p => ({ ...p, ...updates }));
    showToast(`Invoice ${num} issued for €${cost.toFixed(2)}`);
  }

  const rental   = form.rentalId ? rentals.find(r => r.id === form.rentalId) : null;
  const vehicle  = vehicles.find(v => v.id === form.vid);
  const linkedInvoice = form.invoiceId ? invoices.find(i => i.num === form.invoiceId) : null;

  return (
    <Modal open={!!open} size="lg" onClose={() => closeModal('damage')} overlayStyle={{ zIndex: 200 }}>
      <ModalHeader
        title={isEdit ? `Damage ${form.id || dmgId}` : 'Report Damage'}
        subtitle={vehicle?.name || (isEdit ? 'Edit damage record' : 'Log a new damage incident')}
        onClose={() => closeModal('damage')}
      />
      <ModalBody>
        <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onPhotoFile} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* ── Vehicle / Rental / Customer ─────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="fg" style={{ margin: 0 }}>
              <label className="fl">Vehicle *</label>
              <select className="fi" {...F('vid')}>
                <option value="">— Select vehicle —</option>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.name || v.plate}</option>)}
              </select>
            </div>
            <div className="fg" style={{ margin: 0 }}>
              <label className="fl">Link to rental (optional)</label>
              <select className="fi" value={form.rentalId} onChange={e => pickRental(e.target.value)}>
                <option value="">— None / unknown —</option>
                {rentals
                  .filter(r => !form.vid || r.vid === form.vid)
                  .slice(0, 200)
                  .map(r => (
                    <option key={r.id} value={r.id}>
                      {r.id} · {r.customer} · {r.startLabel || r.start}
                    </option>
                  ))}
              </select>
            </div>
            <div className="fg" style={{ margin: 0 }}>
              <label className="fl">Customer name</label>
              <input type="text" className="fi" placeholder="Auto-filled from rental"
                {...F('customerName')} />
            </div>
            <div className="fg" style={{ margin: 0 }}>
              <label className="fl">Reported date</label>
              <input type="date" className="fi" {...F('reportedDate')} />
            </div>
          </div>

          {/* ── Type / Location / Severity ──────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div className="fg" style={{ margin: 0 }}>
              <label className="fl">Type *</label>
              <select className="fi" {...F('type')}>
                {DAMAGE_TYPES.map(t => <option key={t.id} value={t.id}>{t.icon} {t.label}</option>)}
              </select>
            </div>
            <div className="fg" style={{ margin: 0 }}>
              <label className="fl">Location on vehicle</label>
              <select className="fi" {...F('location')}>
                <option value="">— Select location —</option>
                {DAMAGE_LOCATIONS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div className="fg" style={{ margin: 0 }}>
              <label className="fl">Severity</label>
              <div style={{ display: 'flex', gap: 4 }}>
                {DAMAGE_SEVERITIES.map(s => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setForm(p => ({ ...p, severity: s.id }))}
                    style={{
                      flex: 1, padding: '7px 4px', border: `2px solid ${form.severity === s.id ? s.color : 'var(--bd)'}`,
                      borderRadius: 6, background: form.severity === s.id ? s.color + '22' : 'var(--sur)',
                      color: form.severity === s.id ? s.color : 'var(--tx2)',
                      fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ── Description ─────────────────────────────────────────────── */}
          <div className="fg" style={{ margin: 0 }}>
            <label className="fl">Description *</label>
            <textarea className="fi" rows={3} placeholder="e.g. Long scratch along front-left fender after parking incident at Limassol Marina"
              {...F('description')} />
          </div>

          {/* ── Photos ──────────────────────────────────────────────────── */}
          <div>
            <label className="fl">Photos ({(form.photos || []).filter(Boolean).length}/{MAX_PHOTOS})</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8 }}>
              {Array.from({ length: MAX_PHOTOS }).map((_, idx) => {
                const src = (form.photos || [])[idx];
                return (
                  <div key={idx} style={{
                    aspectRatio: '4/3',
                    border: `2px ${src ? 'solid var(--bd)' : 'dashed var(--bd)'}`,
                    borderRadius: 8, overflow: 'hidden',
                    background: src ? 'transparent' : 'var(--sur2)',
                    position: 'relative',
                  }}>
                    {src ? (
                      <>
                        <img src={src} alt={`Photo ${idx + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                          onClick={() => window.open(src, '_blank')} />
                        <button type="button" onClick={() => removePhoto(idx)}
                          style={{ position: 'absolute', top: 3, right: 3, width: 20, height: 20, borderRadius: '50%', background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 10 }}>×</button>
                      </>
                    ) : (
                      <button type="button" onClick={() => openPhotoSlot(idx)}
                        style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 10, color: 'var(--tx3)', background: 'none', border: 'none' }}>
                        <div style={{ fontSize: 18 }}>📷</div>
                        <div>Add</div>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Cost ────────────────────────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
            <div className="fg" style={{ margin: 0 }}>
              <label className="fl">Estimated cost (€)</label>
              <input type="number" className="fi" min="0" step="0.01" placeholder="0" {...F('estimatedCost')} />
            </div>
            <div className="fg" style={{ margin: 0 }}>
              <label className="fl">Final cost (€)</label>
              <input type="number" className="fi" min="0" step="0.01" placeholder="(if known)" {...F('finalCost')} />
            </div>
            <div className="fg" style={{ margin: 0 }}>
              <label className="fl">Status</label>
              <select className="fi" {...F('status')}>
                {DAMAGE_STATUSES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div className="fg" style={{ margin: 0 }}>
              <label className="fl">Paid by</label>
              <select className="fi" {...F('paidBy')}>
                {DAMAGE_PAID_BY.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </div>
          </div>

          {/* ── Invoice / Insurance / Notes ─────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div className="fg" style={{ margin: 0 }}>
              <label className="fl">Pro forma number</label>
              <input type="text" className="fi" placeholder="Linked pro forma" {...F('invoiceId')} />
            </div>
            <div className="fg" style={{ margin: 0 }}>
              <label className="fl">Insurance claim #</label>
              <input type="text" className="fi" placeholder="(if applicable)" {...F('insuranceClaim')} />
            </div>
            <div className="fg" style={{ margin: 0 }}>
              <label className="fl">Status closed date</label>
              <input type="date" className="fi" {...F('resolvedDate')} />
            </div>
          </div>

          {/* ── Repair status (physical state of vehicle) ──────────────── */}
          <div style={{
            background: form.repaired ? 'var(--gr-l)' : 'var(--am-l)',
            border: `1px solid ${form.repaired ? 'var(--gr-b)' : 'var(--am-b)'}`,
            borderRadius: 8, padding: '10px 12px',
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: form.repaired ? 'var(--gr)' : 'var(--am)' }}>
                {form.repaired ? '✅ Vehicle has been repaired' : '🔧 Repair status — still visible on the vehicle'}
              </div>
              <div style={{ fontSize: 10, color: 'var(--tx2)', marginTop: 2 }}>
                {form.repaired
                  ? 'This damage no longer shows as pre-existing on future rentals.'
                  : 'This damage will be shown to operators on the next rental check-in so they don\'t charge the new customer for it.'}
              </div>
            </div>
            {form.repaired && (
              <div className="fg" style={{ margin: 0, width: 140 }}>
                <label className="fl">Repaired date</label>
                <input type="date" className="fi" {...F('repairedDate')} style={{ padding: '5px 8px', fontSize: 11 }} />
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                const nowRepaired = !form.repaired;
                setForm(p => ({
                  ...p,
                  repaired: nowRepaired,
                  repairedDate: nowRepaired ? (p.repairedDate || new Date().toISOString().slice(0, 10)) : '',
                }));
              }}
              style={{
                padding: '8px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                background: form.repaired ? '#fff' : 'var(--gr)', color: form.repaired ? 'var(--tx2)' : '#fff',
                fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                boxShadow: form.repaired ? '0 1px 3px rgba(0,0,0,.1)' : 'none',
              }}
            >
              {form.repaired ? '↺ Mark as unrepaired' : '✓ Mark as repaired'}
            </button>
          </div>

          <div className="fg" style={{ margin: 0 }}>
            <label className="fl">Internal notes</label>
            <textarea className="fi" rows={2} placeholder="Private notes (not shown to customer)" {...F('notes')} />
          </div>

          {/* ── Invoice link / shortcut ─────────────────────────────────── */}
          {(form.customerName && form.vid && (parseFloat(form.finalCost) || parseFloat(form.estimatedCost) || 0) > 0) && (
            <div style={{
              background: linkedInvoice ? 'var(--gr-l)' : 'var(--bl-l)',
              border: `1px solid ${linkedInvoice ? 'var(--gr-b)' : 'var(--bl-b)'}`,
              borderRadius: 8, padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <div style={{ flex: 1, fontSize: 12 }}>
                {linkedInvoice ? (
                  <>
                    <strong>✓ Invoiced</strong> · {linkedInvoice.num} · €{(linkedInvoice.total || 0).toFixed(2)}
                  </>
                ) : (
                  <>💳 Charge customer €{(parseFloat(form.finalCost) || parseFloat(form.estimatedCost) || 0).toFixed(2)} for this damage?</>
                )}
              </div>
              {linkedInvoice ? (
                <button className="btn btn-ghost btn-sm" onClick={() => { closeModal('damage'); openModal('view-invoice', linkedInvoice.id); }}>
                  View invoice
                </button>
              ) : (
                <button className="btn btn-primary btn-sm" onClick={handleIssueInvoice}>
                  Issue invoice
                </button>
              )}
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        {isEdit && (
          <button className="btn btn-ghost" style={{ color: 'var(--ac)', marginRight: 'auto' }} onClick={handleDelete}>🗑 Delete</button>
        )}
        <button className="btn btn-ghost" onClick={() => closeModal('damage')}>Cancel</button>
        <button className="btn btn-primary" onClick={submit}>{isEdit ? 'Save Changes' : 'Report Damage'}</button>
      </ModalFooter>
    </Modal>
  );
}
